import { createContext, FC, ReactNode, useCallback, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import { auth } from "src/firebase";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from "firebase/auth";
import PageLoading from "src/components/PageLoading";
import useSnackbar from "src/hooks/useSnackbar";

interface FirebaseAuthContextState {
  isInitialized: boolean;
  isAuthenticated: boolean;
}

interface FirebaseAuthContextValue extends FirebaseAuthContextState {
  signInWithEmailAndPasswordFB: (email: string, password: string) => Promise<void>;
  signOutFB: () => Promise<void>;
}

enum ActionTypes {
  authStateChanged,
}

type AuthStateChangedAction = {
  type: ActionTypes.authStateChanged;
  payload: {
    isAuthenticated: boolean;
  };
};

const reducer = (state: FirebaseAuthContextState, action: AuthStateChangedAction): FirebaseAuthContextState => {
  switch (action.type) {
    case ActionTypes.authStateChanged:
      const { isAuthenticated } = action.payload;
      return {
        ...state,
        isAuthenticated,
        isInitialized: true,
      };

    default:
      return { ...state };
  }
};

const initialFirebaseAuthState: FirebaseAuthContextState = {
  isAuthenticated: false,
  isInitialized: false,
};

const FirebaseAuthContext = createContext<FirebaseAuthContextValue>({
  ...initialFirebaseAuthState,
  signInWithEmailAndPasswordFB: async () => {},
  signOutFB: async () => {},
});

interface FirebaseAuthProviderProps {
  children: ReactNode;
}

const FirebaseAuthProvider: FC<FirebaseAuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialFirebaseAuthState);
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const { claims } = await auth.currentUser.getIdTokenResult(true);
        if (!claims.admin) {
          showSnackbar("You have no access to this content", "error");
          await signOutFB();
          return;
        }
        dispatch({
          type: ActionTypes.authStateChanged,
          payload: {
            isAuthenticated: true,
          },
        });
      } else {
        dispatch({
          type: ActionTypes.authStateChanged,
          payload: {
            isAuthenticated: false,
          },
        });
      }
    });

    return unsubscribe;
  }, [dispatch, showSnackbar]);

  const signInWithEmailAndPasswordFB = useCallback(async (email: string, password: string): Promise<void> => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signOutFB = async (): Promise<void> => {
    await signOut(auth);
  };

  if (!state.isInitialized) return <PageLoading />;

  return <FirebaseAuthContext.Provider value={{ ...state, signInWithEmailAndPasswordFB, signOutFB }}>{children}</FirebaseAuthContext.Provider>;
};

FirebaseAuthProvider.propTypes = {
  children: PropTypes.node,
};

export { FirebaseAuthContext, FirebaseAuthProvider };
